// hooks/useRolePermissions.js
// Maps Account → Role → UserProfile hierarchy to permission matrix

import { useMemo } from "react";
import { useSession } from "hooks/useSession";

/**
 * Maps your Django Role model names to permission keys used in tableConfigs.
 *
 * Django model hierarchy (from UserProfile):
 *   main_admin → admin → white_label → super_distributor → distributor → retailer
 *
 * Usage:
 *   const { can, roleName, isAdmin } = useRolePermissions(TicketManagement);
 *   if (can.create) { ... }
 */
export const useRolePermissions = (config) => {
  const { role, userid } = useSession(); // role comes from your session/JWT

  const roleName = useMemo(() => {
    if (!role) return "retailer"; // safest default

    // Normalise various formats your backend may return
    const r = String(role).toLowerCase().trim().replace(/ /g, "_");

    const aliasMap = {
      // main admin aliases
      "main_admin":  "main_admin",
      "mainadmin":   "main_admin",
      "superadmin":  "main_admin",
      "super_admin": "main_admin",
      "1":           "main_admin",

      // admin aliases
      "admin":         "admin",
      "administrator": "admin",
      "manager":       "admin",
      "api_partner":   "api_partner",
      "platform_user": "platform_user",
      "payout_api":    "admin",
      "2":             "admin",

      // white label
      "white_label": "white_label",
      "whitelabel":  "white_label",
      "wl":          "white_label",
      "3":           "white_label",

      // super distributor
      "super_distributor":  "super_distributor",
      "superdistributor":   "super_distributor",
      "master_distributor": "super_distributor",
      "masterdistributor":  "super_distributor",
      "sd":                 "super_distributor",
      "4":                  "super_distributor",

      // distributor
      "distributor": "distributor",
      "distributer": "distributor",
      "dist":        "distributor",
      "5":           "distributor",

      // retailer (default)
      "retailer": "retailer",
      "merchant": "retailer",
      "user":     "retailer",
      "6":        "retailer",
    };

    return aliasMap[r] || "retailer";
  }, [role]);

  const can = useMemo(() => {
    const perms = config?.permissions?.[roleName];
    if (!perms) {
      return { create: true, edit: true, delete: true, view: true, export: true, assign: false };
    }
    return {
      create: perms.canCreate  ?? true,
      edit:   perms.canEdit    ?? true,
      delete: perms.canDelete  ?? true,
      view:   perms.canView    ?? true,
      export: perms.canExport  ?? true,
      assign: perms.canAssign  ?? false,
    };
  }, [roleName, config]);

  return {
    can,
    roleName,
    userid,
    isMainAdmin:        roleName === "main_admin",
    isAdmin:            roleName === "admin" || roleName === "main_admin",
    isWhiteLabel:       roleName === "white_label",
    isSuperDistributor: roleName === "super_distributor",
    isDistributor:      roleName === "distributor",
    isRetailer:         roleName === "retailer",
  };
};